import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Button, Spinner, Alert, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDate, formatDateTime } from '../utils/commonUtils'
import ResizableTable from './ResizableTable'
import PaginationComponent from './Pagination'
import SearchInputWithClear from './SearchInputWithClear'
import CopyableText from './CopyableText'
import StockItemInfo from './StockItemInfo'
import { AmazonSettingsSectionHeader } from './AmazonScopeChip'
import AmazonProductExceptionModal, {
    PRODUCT_EXCEPTION_SCOPE, TAKES_EFFECT_NOTE, formatAcos,
} from './AmazonProductExceptionModal'

/**
 * Product exceptions tab of Ad Automation Settings: per-product target ACoS or exclusion, used by
 * Anomaly Detection only. The page owns the account and the search (kept in the URL).
 */
function AmazonProductExceptionsPanel({ accountId, canManage, search, onSearchChange }) {
    const [searchInputValue, setSearchInputValue] = useState(search || '')
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [rows, setRows] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [modal, setModal] = useState({ show: false, row: null })
    const debounceRef = useRef(null)

    useEffect(() => { setSearchInputValue(search || ''); setCurrentPage(1) }, [search, accountId])

    const loadRows = useCallback(async () => {
        if (!accountId) return
        setLoading(true)
        try {
            const params = { account_id: accountId, page: currentPage, page_size: pageSize }
            if (search) params.search = search
            const data = await amazonAPI.anomalySettings.getProducts(params)
            setRows(data?.results || [])
            setTotalItems(data?.count || 0)
            setError('')
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [accountId, currentPage, pageSize, search])

    useEffect(() => { loadRows() }, [loadRows])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => onSearchChange(value), 500)
    }
    const handleSearchClear = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setSearchInputValue('')
        onSearchChange('')
    }

    const columns = [
        { id: 'image', header: 'Image', cell: (row) => row.stock_item_detail?.image ? (
            <OverlayTrigger
                placement="right"
                overlay={
                    <Tooltip className="stock-item-tooltip">
                        <StockItemInfo stockItem={row.stock_item_detail} />
                    </Tooltip>
                }
            >
                <img
                    src={row.stock_item_detail.image}
                    alt="Stock Item Thumbnail"
                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                />
            </OverlayTrigger>
        ) : null },
        { id: 'asin', header: 'ASIN', cell: (row) => <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={search} /> },
        { id: 'sku', header: 'SKU', cell: (row) => row.amazon_sku ? <CopyableText value={row.amazon_sku} toastLabel="SKU" searchTerm={search} /> : '' },
        { id: 'gtin', header: 'GTIN', cell: (row) => row.stock_item_detail?.gtin ? <CopyableText value={row.stock_item_detail.gtin} toastLabel="GTIN" searchTerm={search} /> : '' },
        { id: 'product', header: 'Description', cell: (row) => <span style={{ whiteSpace: 'normal' }}>{row.product_name || ''}</span> },
        { id: 'break_even', header: 'Break-even ACoS', cell: (row) => formatAcos(row.break_even_acos) },
        { id: 'target', header: 'Target ACoS', cell: (row) => row.excluded ? '' : formatAcos(row.target_acos) },
        { id: 'excluded', header: 'Excluded', cell: (row) => row.excluded ? <Badge bg="secondary">Excluded</Badge> : '' },
        { id: 'grace', header: 'Launch Grace', cell: (row) => row.in_grace ? `Until ${formatDate(row.grace_until)}` : '' },
        { id: 'note', header: 'Note', cell: (row) => <span style={{ whiteSpace: 'normal' }}>{row.note || ''}</span> },
        { id: 'updated', header: 'Updated', cell: (row) => (
            <div className="small">
                <div>{formatDateTime(row.updated_at)}</div>
                {row.updated_by_username && <div className="text-body-secondary">{row.updated_by_username}</div>}
            </div>
        )},
        { id: 'actions', header: 'Actions', cell: (row) => (
            <Button variant="primary" size="sm" onClick={() => setModal({ show: true, row })}>Details</Button>
        )},
    ]

    return (
        <div>
            <AmazonSettingsSectionHeader
                title="Product exceptions"
                scope="anomaly"
                scopeLabel="Anomaly Detection · per product"
                sentence={`${PRODUCT_EXCEPTION_SCOPE} Products whose first ad spend is less than 45 days old are skipped automatically (launch grace), without an exception.`}
                actions={canManage && (
                    <Button variant="primary" onClick={() => setModal({ show: true, row: null })} disabled={!accountId}>
                        <i className="fas fa-plus me-2"></i>Add product exception
                    </Button>
                )}
            />
            {canManage && <div className="small text-body-secondary mb-3">{TAKES_EFFECT_NOTE}</div>}

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="mb-3" style={{ maxWidth: '420px' }}>
                <SearchInputWithClear
                    value={searchInputValue}
                    onChange={handleSearchChange}
                    onClear={handleSearchClear}
                    placeholder="Search ASIN, SKU, description, GTIN, note..."
                />
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : rows.length === 0 ? (
                <Alert variant="info">No product exceptions{search ? ' match this search' : ' yet'}.</Alert>
            ) : (
                <>
                    <ResizableTable
                        columns={columns}
                        data={rows}
                        tableId="amazon_product_exceptions"
                        minHeight="auto"
                        striped
                        hover
                        className="table-sm"
                    />
                    <div className="mt-3">
                        <PaginationComponent
                            currentPage={currentPage}
                            totalItems={totalItems}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                        />
                    </div>
                </>
            )}

            <AmazonProductExceptionModal
                show={modal.show}
                accountId={accountId}
                row={modal.row}
                canManage={canManage}
                onClose={() => setModal({ show: false, row: null })}
                onSaved={loadRows}
            />
        </div>
    )
}

export default AmazonProductExceptionsPanel
